import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertTenantFromDemo, type Tenant, type TenantDemoConversion } from "./tenantsApi";
import { conversionErrorKey } from "./tenantDemoConversion";
import { ApiError, SessionExpiredError } from "@web/lib/api";

// THE UNFINISHED CRYPTO-SHRED OF A CONVERSION THAT IS ALREADY IN EFFECT — the `incomplete` ending of
// "Muunna asiakastenantiksi" (task e03c428d), with the count of keys still owed and the action that
// finishes them.
//
// WHY IT IS A COMPONENT OF THE PAGE AND NOT A PART OF `TenantDemoSection`, which is where round 1 put it
// and is the defect this file exists to fix. A conversion that reaches `incomplete` has COMMITTED: the
// dataset's rows are gone and `is_demo` is off. `TenantDemoSection` is rendered only while
// `tenant.isDemo`, and the page re-reads the tenant the moment the conversion answers — so the read that
// proves the conversion worked is the same read that unmounted the one place the outstanding count and the
// "Yritä uudelleen" button were drawn. The operator was left with a one-line banner, no number and no
// retry, over a tenant whose people's Transit keys are still in OpenBao. ADR-0013's rule is that an
// unconfirmed shred is never reported as done; a retry that cannot be reached is the same claim made by
// omission.
//
// So the RESULT belongs to the page: the section reports it up, the page keeps it, and this renders it
// outside the demo-only tree. It survives the re-read, it survives the tenant becoming a customer tenant,
// and it goes away only when a retry confirms there is nothing left to owe.
//
// WHAT IT STILL DOES NOT SURVIVE, stated rather than implied: a document RELOAD. This is page state, like
// every other result banner here, and the tenant read carries no "keys still owed" fact for it to be
// rebuilt from — so an operator who navigates away before retrying has no button to come back to. The
// obligation is not lost with the banner: the outstanding refs are in the API log and on record in
// `demo_legacy_key_shreds` until Transit confirms each one absent, and `POST …/convert-from-demo` on the
// now-customer tenant is still the retry. Making the page able to offer it on a later visit needs that
// fact on the tenant read, which is a field this task did not have; `docs/infra/platform-console.md` says
// so where an operator will look.
//
// THE RETRY IS THE SAME ROUTE AS THE CONVERSION, with no dialog. By now there is nothing left to confirm —
// the rows the dialog warned about are already deleted — and on a tenant whose mark is off that POST
// retries nothing but the shred (`shred_retried`). Its answer goes back through the same `onRetried` the
// conversion used, which is what lets a retry that STILL cannot confirm a key keep this block on screen
// with its new, smaller count rather than silently claiming success.

export function TenantShredOutstanding({
  tenant,
  result,
  onRetried,
}: {
  tenant: Tenant;
  /** The conversion (or the last retry) whose keys are still outstanding. */
  result: TenantDemoConversion;
  /**
   * The retry answered. The page does with it exactly what it did with the conversion's own answer: says
   * the sentence and decides whether anything is still owed — so one place decides, and a retry cannot
   * report a completeness the conversion would not have.
   */
  onRetried: (result: TenantDemoConversion) => void;
}) {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const onRetry = useCallback(() => {
    if (retrying) return;
    setFailed(null);
    setRetrying(true);
    convertTenantFromDemo(tenant.id)
      .then(onRetried)
      .catch((err: unknown) => {
        if (err instanceof SessionExpiredError) return;
        setFailed(
          err instanceof ApiError
            ? t(conversionErrorKey(err.status, err.problem))
            : t("tenants.demo.error"),
        );
      })
      .finally(() => setRetrying(false));
  }, [retrying, tenant.id, onRetried, t]);

  return (
    <div className="banner banner--error" role="alert" data-testid="tenant-demo-incomplete">
      <p>{t("tenants.demo.incompleteDetail", { rows: Number(result.keysOutstanding) })}</p>

      {failed && (
        <p className="field-error" data-testid="tenant-demo-retry-error">
          {failed}
        </p>
      )}

      <button
        type="button"
        className="btn"
        onClick={onRetry}
        disabled={retrying}
        data-testid="tenant-demo-retry"
      >
        {retrying ? t("tenants.demo.retrying") : t("tenants.demo.retry")}
      </button>
    </div>
  );
}
